/* eslint-disable react/no-multi-comp */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import VelocityTransitionGroup from 'velocity-react/velocity-transition-group';

// Services
import Agreement from 'services/data/agreement';
import Ticket from 'services/data/ticket';
import Person from 'services/data/person';
import TimeReport from 'services/data/time-report';
import PurchaseOrder from 'services/data/purchase-order';

// Actions
import { alerts } from 'modules/alerts/actions';

// Components
import SVG from 'components/widgets/icons/svg';
import { Form, FormConfirm, FormConfirmAction, FormConfirmActions } from 'components/forms/form-structure';
import CompanySelect from 'components/forms/selects/company-select';
import AgreementSelect from 'components/forms/selects/agreement-select';
import TicketSelect from 'components/forms/selects/ticket-select';
import TicketLink from 'components/models/ticket/ticket-link';

// Helpers
import requiredValidation from 'utils/required-validation';
import { slideClosed, slideOpen } from 'utils/transition-groups';


class MergeTicketForm extends React.Component {
  static propTypes = {
    formValues: PropTypes.object.isRequired,
    afterSubmit: PropTypes.object,
    closeOnSubmit: PropTypes.bool,
    ticketToMerge: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    initialValues: PropTypes.object.isRequired,
    currentUser: PropTypes.object,
    companies: PropTypes.array,
    change: PropTypes.func
  };

  constructor() {
    super();
    this.state = { openTickets: [] };

    this.submit = this.submit.bind(this);
  }

  componentWillMount() {
    const { ticketToMerge } = this.props;

    // Get the current number of time reports and purchases so we know if we need
    // to confirm when we are updating
    return Promise.all([
      TimeReport.count({ where: { ticket: ticketToMerge.id, invoice: null } }),
      PurchaseOrder.count({ where: { ticket: ticketToMerge.id, invoice: null } })
    ])
      .then(([time, purchases]) => {
        this.props.change('ticketCurrentTime', time);
        this.props.change('ticketCurrentPurchases', purchases);
      });
  }

  submit(values) {
    const { afterSubmit, closeOnSubmit, close, ticketToMerge } = this.props;

    const updates = {
      ticketToMerge: ticketToMerge.id,
      mergeInto: values.ticket,
      updateAgreement: values.updateAgreement,
      updateTimeBillable: values.updateTimeBillable
    };

    return Ticket.merge(updates)
      .then(() => {
        alerts.success({
          title: 'Tickets merged!',
          subtitle: <TicketLink ticket={this.state.fullTicket}>View ticket</TicketLink>
        });
        
        if (closeOnSubmit) close();
        if (afterSubmit) return afterSubmit(this.values);
      })
      .catch((err) => {
        alerts.catchError(err);
      });
  }

  render() {
    const { handleSubmit, close, ticketToMerge, currentUser, change, ...props } = this.props;
    const { fullTicket } = this.state;
    const { company, agreement, updateAgreement, updateTimeBillable, ticketCurrentPurchases, ticketCurrentTime } = this.props.formValues;
    const isLead = Person.isLead(currentUser);
    const newAgreementId = props.formValues.agreement;
    const oldAgreementId = _.get(ticketToMerge, 'agreement.id');
    const changingAgreement = newAgreementId && newAgreementId !== oldAgreementId;
    const requireAgreementConfirm = changingAgreement && !!(ticketCurrentTime || ticketCurrentPurchases);
    const newBillable = _.get(fullTicket, 'timeDefaultBillable');
    const oldBillable = _.get(ticketToMerge, 'timeDefaultBillable');
    const changingBillable = !_.isUndefined(newBillable) && newBillable !== oldBillable;
    const requireBillableConfirm = changingBillable && !!ticketCurrentTime;
    const billableLabel = newBillable ? 'billable' : 'non-billable';
    const mergeToBizDevError = oldAgreementId && fullTicket && !_.get(fullTicket, 'agreement');

    return (
      <div>
        <div className="form-header">
          <SVG src="svg/merge.svg" className="merge" />
          <h5 className="form-title">Merge Ticket</h5>
          { close && <SVG onClick={close} className="close" src="svg/close.svg" /> }
        </div>
        <Form onSubmit={handleSubmit(this.submit)} {..._.pick(props, ['submitting', 'error', 'submitFailed'])} customSubmitArea={((changingAgreement || requireBillableConfirm) && !isLead) || mergeToBizDevError}>
          { (changingAgreement || requireBillableConfirm) && !isLead &&
            <div className="success-block notification-block">Great, we love merging tickets and keeping things organized! Changing agreements can be dangerous from an accounting perspective, so please talk to a team lead or principal to get this done!</div>
          }
          <Field component={CompanySelect} name="company" label="Company" />
          { company &&
            <Field component={AgreementSelect} name="agreement" label="Agreement" company={company} searchParams={Agreement.activeQuery} />
          }
          <VelocityTransitionGroup leave={slideClosed} enter={slideOpen}>
            { requireAgreementConfirm && isLead &&
              <FormConfirm name="updateAgreement" title={'Update agreement for current time and purchase orders?'}>
                <div className="form-confirm-description positive">
                  Select <span className="form-confirm-emphasize positive">yes</span> to update all current time reports and purchase orders in this ticket to the new agreement.
                  Most commonly used for:
                  <ul>
                    <li>Mistaken ticketing of a triage item</li>
                    <li>Short-lived duplicate tickets</li>
                  </ul>
                </div>
                <div className="form-confirm-description">
                  Select <span className="form-confirm-emphasize">no</span> to leave all current time reports and purchase orders in this ticket as-is, new time reports and purchase orders will default to the new agreement.
                  Most commonly used for:
                  <ul>
                    <li>Rolling tickets into a new ongoing agreement</li>
                    <li>Merging long-lived related tickets</li>
                  </ul>
                </div>
                <FormConfirmActions>
                  <FormConfirmAction onClick={() => change('updateAgreement', true)} active={updateAgreement === true} label="Yes" svg="svg/check.svg">Move all time and purchases to the new agreement. <span className="form-confirm-emphasize positive">This will affect invoices, rates, and realization!</span></FormConfirmAction>
                  <FormConfirmAction onClick={() => change('updateAgreement', false)} active={updateAgreement === false} label="No" svg="svg/archived.svg">Leave all current time and purchases as-is. This will not affect invoices, rates, and realization.</FormConfirmAction>
                </FormConfirmActions>
              </FormConfirm>
            }
          </VelocityTransitionGroup>
          { company && agreement &&
            <div>
              <Field
                component={TicketSelect}
                name="ticket"
                label="Ticket"
                agreement={agreement}
                company={company}
                disabled={changingAgreement && !isLead}
                customWhere={{ status: 'Active', id: { '!': ticketToMerge.id } }}
                onChangeFullValue={(ticket) => {
                  this.setState({ fullTicket: ticket });
                  this.props.change('timeDefaultBillable', _.get(ticket, 'timeDefaultBillable'));
                }}
              />
            </div>
          }
          { mergeToBizDevError &&
            <div className="success-block notification-block">You cannot merge a ticket with an agreement into a ticket without and agreement, please select a different ticket!</div>
          }
          <VelocityTransitionGroup leave={slideClosed} enter={slideOpen}>
            { requireBillableConfirm && isLead &&
              <FormConfirm name="updateTimeBillable" title={`Update current time reports to be ${billableLabel}?`}>
                <div className="form-confirm-description positive">Select <span className="form-confirm-emphasize positive">yes</span> to update all current time reports in this ticket to be {billableLabel}.</div>
                <div className="form-confirm-description">Select <span className="form-confirm-emphasize">no</span> to leave all current time reports in this ticket as-is, new time reports will default to {billableLabel}.</div>
                <FormConfirmActions>
                  <FormConfirmAction onClick={() => change('updateTimeBillable', true)} active={updateTimeBillable === true} label="Yes" svg="svg/check.svg">Make all current time {billableLabel}. <span className="form-confirm-emphasize positive">This will affect invoices, rates, and realization!</span></FormConfirmAction>
                  <FormConfirmAction onClick={() => change('updateTimeBillable', false)} active={updateTimeBillable === false} label="No" svg="svg/archived.svg">Leave all current time as-is. This will not affect invoices, rates, and realization.</FormConfirmAction>
                </FormConfirmActions>
              </FormConfirm>
            }
          </VelocityTransitionGroup>
        </Form>
      </div>
    );
  }
}

const ConnectedComponent = connect((state, ownProps) => ({
  formValues: formValueSelector(ownProps.form)(state, 'company', 'agreement', 'ticket', 'updateTimeBillable', 'updateAgreement', 'ticketCurrentTime', 'ticketCurrentPurchases'),
  companies: state.pageData.selectCompanies,
  currentUser: state.auth.currentUser
}))(MergeTicketForm);

export default reduxForm({
  form: 'merge-ticket-form',
  validate: (values, props) => {
    const errors = requiredValidation(['company', 'agreement', 'ticket'], values);

    const updatingBillable = values.timeDefaultBillable !== props.ticketToMerge.timeDefaultBillable && values.ticketCurrentTime;
    const updateTimeBillableUndefined = _.isUndefined(values.updateTimeBillable);

    if (updatingBillable && updateTimeBillableUndefined) errors.updateTimeBillable = 'Required';

    const newAgreementId = values.agreement;
    const oldAgreementId = _.get(props.ticketToMerge, 'agreement.id');
    const changingAgreement = newAgreementId !== oldAgreementId && (values.ticketCurrentTime || values.ticketCurrentPurchases);
    const updateAgreementUndefined = _.isUndefined(values.updateAgreement);

    if (changingAgreement && updateAgreementUndefined) errors.updateAgreement = 'Required';

    return errors;
  }
})(ConnectedComponent);



// WEBPACK FOOTER //
// ./app/components/models/ticket/forms/merge-ticket-form.js